import moment from 'moment'
import * as React from 'react'

import SimpleModal from './SimpleModal'

import { useDirtySanchez } from '../components/DirtySanchez'
import Select from '../components/Select'
import { Props } from '../interfaces/components/project-dialog'
import { Campaign } from '../interfaces/entities/campaign'
import { Client } from '../interfaces/entities/client'
import { Contact } from '../interfaces/entities/contact'
import {
  isSavedProject,
  Project,
  unsavedProject,
  UnsavedProjectType,
} from '../interfaces/entities/project'
import { IProjectService } from '../interfaces/services/projects'

import DI from '../app/di'
import { translateWithUserLocale } from '../utils/i18n'
import { stripSpecialCharacters } from '../utils/string'

import { Box, Button, InputLabel, TextField, styled } from '@design/pt-ui'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import Loader from './Loader'

export const projectColumns = [
  { name: 'client', title: 'Client' },
  { name: 'project', title: 'Project' },
  { name: 'campaign', title: 'Campaigns' },
  { name: 'deadline', title: 'Deadline Delivery' },
  { name: 'status', title: 'Status' },
  { name: 'action', title: 'Action' },
]

const FieldGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column',
  margin: theme.spacing(4),
  textAlign: 'left',
}))

const ErrorText = styled('span')({
  fontSize: 14,
  fontWeight: 400,
  color: 'red',
  marginLeft: 20,
})

export interface IProps extends Props {
  open?: boolean
}

interface IState {
  open: boolean
  loading: ('project' | 'clients' | 'client' | 'campaigns')[]
  saving: boolean
  error: string | null
  data: Project | UnsavedProjectType
  errorMessages: {
    client: string | null
    name: string | null
    deadline: string | null
    pmContacts: string | null
  }

  options: {
    campaigns: Campaign[] | null
    clients: Client[] | null
    contacts: Contact[] | null
    pmContacts: Contact[] | null
  }
}

function ProjectDialog(props: IProps) {
  const getInitialState: (props: IProps) => IState = (props: IProps) => {
    return {
      open: props.open || false,
      loading: props.projectId ? ['project', 'clients'] : ['clients'],
      saving: false,
      error: null,
      errorMessages: {
        client: null,
        name: null,
        deadline: null,
        pmContacts: null,
      },
      data: props.project || unsavedProject(),
      options: {
        campaigns: null,
        clients: null,
        contacts: null,
        pmContacts: null,
      },
    }
  }

  const getProjectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  const initialize = async () => {
    const projectService = getProjectService()
    if (props.projectId) {
      const projectResponse = await projectService.getProject(props.projectId)
      if (projectResponse.type === 'error') {
        setState((state) => ({
          ...state,
          loading: state.loading.filter((l) => l !== 'project'),
          error: 'Could not load project.',
        }))
      } else {
        const project = projectResponse.payload
        setState((state) => ({
          ...state,
          loading: state.loading.filter((l) => l !== 'project'),
          data: project,
          options: {
            ...state.options,
            campaigns: project.campaigns,
          },
        }))
      }
    }

    if (props.project && props.project.client) {
      await getCampaigns(props.project.client)
      await getContacts(props.project.client)
    }

    const clientsResponse = await projectService.getClients()
    if (clientsResponse.type === 'error') {
      setState((state) => ({
        ...state,
        loading: state.loading.filter((l) => l !== 'clients'),
        error: 'Could not load clients.',
      }))
    } else {
      setState((state) => ({
        ...state,
        data: props.project || state.data,
        loading: state.loading.filter((l) => l !== 'clients'),
        options: {
          ...state.options,
          clients: clientsResponse.payload,
        },
      }))
    }
  }

  const [state, setState] = React.useState<IState>(getInitialState(props))
  const { isAnyDirty, clean, defile } = useDirtySanchez()

  const handleClientChange = async (value: any, defile: (id: any) => any) => {
    if (state.options.clients === null) {
      return
    }

    const clientId = value.value as string
    const client = state.options.clients.find((c) => c.id === clientId) || null
    if (client == null) {
      return
    }

    setState((state) => ({
      ...state,
      errorMessages: { ...state.errorMessages, client: null },
      data: {
        ...state.data,
        client,
        campaigns: [],
        contacts: [],
        pmContacts: [],
      },
      loading: [...state.loading, 'campaigns'],
      options: {
        ...state.options,
        contacts: client.contacts,
        pmContacts: client.pmContacts,
        campaigns: null,
      },
    }))

    await getCampaigns(client)

    var projectId = ''
    if (isSavedProject(state.data)) {
      projectId = state.data.id
    }
    const clientResponse = await getProjectService().getClient(
      clientId,
      true,
      projectId,
    )
    if (clientResponse.type === 'success') {
      const clientWithContacts = clientResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          contacts: clientWithContacts.contacts,
          pmContacts: clientWithContacts.pmContacts,
        },
      }))
    }
    defile('project')
  }

  const getCampaigns = async (client: Client) => {
    const campaignsResponse = await getProjectService().getCampaigns(client)
    if (campaignsResponse.type === 'success') {
      const campaigns = campaignsResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          campaigns: campaigns,
        },
      }))
    }
  }

  const getContacts = async (client: Client) => {
    var projectId = ''
    if (isSavedProject(state.data)) {
      projectId = state.data.id
    }
    const clientResponse = await getProjectService().getClient(
      client.id,
      true,
      projectId,
    )
    if (clientResponse.type === 'success') {
      const clientWithContacts = clientResponse.payload
      setState((state) => ({
        ...state,
        options: {
          ...state.options,
          contacts: clientWithContacts.contacts,
          pmContacts: clientWithContacts.pmContacts,
        },
      }))
    }
  }

  const handleCampaignChange = (value: any, defile: (id: any) => any) => {
    if (state.options.campaigns === null) {
      return
    }

    const campaignIds = value.map((value: any) => value.value) as string[]
    const campaigns = campaignIds
      .map((campaignId) => {
        return (
          (state.options.campaigns || []).find((x) => x.id === campaignId) ||
          null
        )
      })
      .filter((x) => x != null) as Campaign[]

    setState((state) => ({
      ...state,
      data: {
        ...state.data,
        campaigns: campaigns,
      },
    }))
    defile('project')
  }

  const onNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    defile: (id: any) => any,
  ) => {
    const value = stripSpecialCharacters(event.target.value)

    setState((state) => ({
      ...state,
      errorMessages: { ...state.errorMessages, name: null },
      data: {
        ...state.data,
        name: value,
      },
    }))
    defile('project')
  }

  const handleClientContactChange = (value: any, defile: (id: any) => any) => {
    if (state.options.contacts === null) {
      return
    }

    const contactsIds = value.map((v: any) => v.value) as string[]
    const contacts = contactsIds
      .map((contactId) => {
        return (
          (state.options.contacts || []).find((x) => x.id === contactId) || null
        )
      })
      .filter((x) => x != null) as Contact[]

    setState((state) => ({
      ...state,
      data: {
        ...state.data,
        contacts: contacts,
      },
    }))
    defile('project')
  }

  const handlePmContactChange = (value: any, defile: (id: any) => any) => {
    if (state.options.pmContacts === null) {
      return
    }

    const pmContactIds = value.map((value: any) => value.value) as string[]
    const pmContacts = pmContactIds
      .map((pmcId) => {
        return (
          (state.options.pmContacts || []).find((x) => x.id === pmcId) || null
        )
      })
      .filter((x) => x != null) as Contact[]

    setState((state) => ({
      ...state,
      errorMessages: { ...state.errorMessages, pmContacts: null },
      data: {
        ...state.data,
        pmContacts: pmContacts,
      },
    }))
    defile('project')
  }

  const handleDeadlineChange = (
    date: moment.Moment | null,
    defile: (id: any) => any,
  ) => {
    setState((state) => ({
      ...state,
      data: {
        ...state.data,
        errorMessages: { ...state.errorMessages, deadline: null },
        deadline: moment(date),
      },
    }))
    defile('project')
  }

  const open = () => {
    setState((state) => ({
      ...state,
      open: true,
    }))

    initialize()
  }

  const close = () => {
    if (state.saving) {
      return
    }

    const initial = getInitialState(props)

    clean('project')
    setState((state) => ({
      ...initial,
      open: false,
      options: {
        ...initial.options,
        clients: state.options.clients,
      },
    }))
  }

  const handleSave = async () => {
    const data = state.data
    setState((state) => ({
      ...state,
      errorMessages: {
        client: null,
        name: null,
        deadline: null,
        pmContacts: null,
      },
    }))
    if (
      data.client &&
      data.name &&
      data.deadline &&
      data.pmContacts.length > 0
    ) {
      setState((state) => ({
        ...state,
        saving: true,
      }))
      setState((state) => ({
        ...state,
        saving: false,
      }))
      close()
      await props.onSave(data, { created: false })
    } else {
      if (data.client == null) {
        setState((state) => ({
          ...state,
          errorMessages: {
            ...state.errorMessages,
            client: 'Field is required',
          },
        }))
      }
      if (data.name === '') {
        setState((state) => ({
          ...state,
          errorMessages: {
            ...state.errorMessages,
            name: 'Field is required',
          },
        }))
      }
      if (data.deadline == null) {
        setState((state) => ({
          ...state,
          errorMessages: {
            ...state.errorMessages,
            deadline: 'Field is required',
          },
        }))
      }
      if (data.pmContacts.length === 0) {
        setState((state) => ({
          ...state,
          errorMessages: {
            ...state.errorMessages,
            pmContacts: 'Field is required',
          },
        }))
      }
    }
  }

  const showLoader = () => {
    return (
      state.loading.includes('project') ||
      state.loading.includes('clients') ||
      state.saving
    )
  }

  return (
    <SimpleModal
      id="project-dialog"
      title={
        isSavedProject(state.data)
          ? translateWithUserLocale('project_modal.edit_project')
          : translateWithUserLocale('project_modal.add_new_project')
      }
      open={state.open}
      onOpen={open}
      onClose={close}
      confirmClose={isAnyDirty}
      activator={props.activator}>
      {showLoader() ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <Loader
            message={translateWithUserLocale('loaders.loading_project_form')}
          />
        </Box>
      ) : (
        <div style={{ width: 640, padding: '0 15px' }}>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-client">
              {translateWithUserLocale('project_modal.client')}
              {state.data.client == null &&
              state.errorMessages.client !== null ? (
                <ErrorText>* {state.errorMessages.client}</ErrorText>
              ) : null}
            </InputLabel>
            <Select
              id="cp-project-dialog-client"
              className="cp-project-dialog-client"
              value={
                state.data.client
                  ? {
                      value: state.data.client.id,
                      label: state.data.client.name,
                    }
                  : undefined
              }
              options={(state.options.clients || []).map((client) => {
                return {
                  value: client.id,
                  label: client.name,
                }
              })}
              onChange={(v: any) => handleClientChange(v, defile)}
            />
          </FieldGroup>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-campaign">
              {translateWithUserLocale('project_modal.campaigns_optional')}
            </InputLabel>
            <Select
              id="cp-project-dialog-campaign"
              className="cp-project-dialog-campaign"
              value={state.data.campaigns.map((campaign) => ({
                value: campaign.id,
                label: campaign.projectNumber
                  ? `${campaign.projectNumber} - ${campaign.name}`
                  : campaign.name,
              }))}
              options={(state.options.campaigns || []).map((campaign) => {
                return {
                  value: campaign.id,
                  label: campaign.projectNumber
                    ? `${campaign.projectNumber} - ${campaign.name}`
                    : campaign.name,
                }
              })}
              isMulti={true}
              isDisabled={state.options.campaigns == null}
              onChange={(v: any) => handleCampaignChange(v, defile)}
            />
          </FieldGroup>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-name">
              {translateWithUserLocale('project_modal.project_name')}
              {state.data.name === '' && state.errorMessages.name !== null ? (
                <ErrorText>* {state.errorMessages.name}</ErrorText>
              ) : null}
            </InputLabel>
            <TextField
              id="cp-project-dialog-name"
              className="cp-project-dialog-name"
              variant="standard"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onNameChange(e, defile)
              }
              value={state.data.name}
            />
          </FieldGroup>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-contact">
              {translateWithUserLocale(
                'project_modal.project_contacts_client_optional',
              )}
            </InputLabel>
            <Select
              id="cp-project-dialog-contact"
              className="cp-project-dialog-contact"
              value={state.data.contacts.map((contact) => {
                return {
                  value: contact.id,
                  label: contact.name,
                }
              })}
              options={(state.options.contacts || [])
                .sort((a, b) => {
                  if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
                  if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
                  return 0
                })
                .map((contact) => {
                  return {
                    value: contact.id,
                    label: contact.name,
                  }
                })}
              isMulti={true}
              isDisabled={state.options.contacts == null}
              onChange={(v: any) => handleClientContactChange(v, defile)}
            />
          </FieldGroup>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-deadline">
              {translateWithUserLocale('project_modal.delivery_deadline')}
              {state.data.deadline === null &&
              state.errorMessages.deadline !== null ? (
                <ErrorText>* {state.errorMessages.deadline}</ErrorText>
              ) : null}
            </InputLabel>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                format="DD.MM.YYYY"
                value={state.data.deadline ? moment(state.data.deadline) : null}
                onChange={(date) => handleDeadlineChange(date, defile)}
                slotProps={{
                  textField: {
                    id: 'cp-project-dialog-deadline',
                    className: 'cp-project-dialog-deadline',
                    fullWidth: true,
                    variant: 'standard',
                  },
                }}
              />
            </LocalizationProvider>
          </FieldGroup>
          <FieldGroup>
            <InputLabel htmlFor="cp-project-dialog-pm-contact">
              {translateWithUserLocale('project_modal.pm_contacts')}
              {state.errorMessages.pmContacts !== null ? (
                <ErrorText>* {state.errorMessages.pmContacts}</ErrorText>
              ) : null}
            </InputLabel>
            <Select
              id="cp-project-dialog-pm-contact"
              className="cp-project-dialog-pm-contact"
              menuPlacement="top"
              maxMenuHeight={250}
              value={state.data.pmContacts.map((contact) => {
                return {
                  value: contact.id,
                  label: contact.name,
                }
              })}
              options={(state.options.pmContacts || [])
                .filter(
                  (pmContact) =>
                    typeof pmContact.email !== 'undefined' &&
                    pmContact.hidden !== true,
                )
                .map((contact) => {
                  return {
                    value: contact.id,
                    label: contact.name,
                  }
                })}
              isMulti={true}
              isDisabled={state.options.pmContacts == null}
              onChange={(v: any) => handlePmContactChange(v, defile)}
            />
          </FieldGroup>
          <FieldGroup>
            <Button variant="contained" sx={{ margin: 1 }} onClick={handleSave}>
              {isSavedProject(state.data)
                ? translateWithUserLocale('project_modal.update_project')
                : translateWithUserLocale('project_modal.create_project')}
            </Button>
          </FieldGroup>
        </div>
      )}
    </SimpleModal>
  )
}

export default ProjectDialog
